/**
 * WeakMap of elements currently animating.
 */
const animatingElements = new WeakMap<
	HTMLElement,
	{
		cancel: () => void;
	}
>();

/**
 * WeakMap of elements that have been initialized/collapsed.
 * @param element
 */
function parseComputedDuration(element: HTMLElement): number {
	const style = getComputedStyle(element);

	let duration = Number(style.transitionDuration.replace(/,.*/, '').replace('s', '')) * 1000;

	if (duration === 0) {
		duration = Number(style.animationDuration.replace(/,.*/, '').replace('s', '')) * 1000;
	}

	const delay = Number(style.transitionDelay.replace(/,.*/, '').replace('s', '')) * 1000;

	return duration + delay;
}

/**
 * Animates slide up.
 *
 * Replacement for jQuery slideUp animation.
 *
 * @param element            The element to slide up.
 * @param options            Animation options.
 * @param options.duration   Duration of the animation in milliseconds (default: 400).
 * @param options.minHeight  Minimum height of the element (default: 0).
 * @param options.transition CSS transition timing function (default: 'cubic-bezier(0.4, 0.0, 0.2, 1)').
 */
const slideUp = (
	element: HTMLElement,
	options: {
		duration?: number;
		minHeight?: number;
		transition?: string;
	} = {}
): Promise<void> => {
	const {
		duration = 400,
		minHeight = 0,
		transition = 'cubic-bezier(0.4, 0.0, 0.2, 1)',
	} = options;

	return new Promise((resolve) => {
		const existing = animatingElements.get(element);
		if (existing) {
			existing.cancel();
		}

		const height = element.getBoundingClientRect().height;

		if (height <= minHeight) {
			resolve();
			return;
		}

		let finished = false;

		const cleanup = () => {
			element.style.removeProperty('transition-property');
			element.style.removeProperty('transition-duration');
			element.style.removeProperty('transition-timing-function');
			animatingElements.delete(element);
		};

		const finish = () => {
			if (finished) return;
			finished = true;

			if (minHeight === 0) {
				element.style.display = 'none';
				element.hidden = true;
			}

			cleanup();
			resolve();
		};

		const cancel = () => {
			if (finished) return;
			finished = true;
			cleanup();
			resolve();
		};

		animatingElements.set(element, { cancel });

		element.style.overflow = 'hidden';
		element.style.height = height + 'px';

		void element.offsetHeight;

		element.style.transitionProperty = 'height';
		element.style.transitionDuration = `${duration}ms`;
		element.style.transitionTimingFunction = transition;

		// double nest to ensure the browser has fully painted before applying end styles.
		requestAnimationFrame(() => {
			if (finished) return;

			requestAnimationFrame(() => {
				if (finished) return;

				element.style.height = minHeight + 'px';

				const totalDuration = parseComputedDuration(element);
				setTimeout(finish, totalDuration);
			});
		});
	});
};

/**
 * Animate slide down
 *
 * Replacement for jQuery slideDown animation.
 *
 * @param element            The element to slide down.
 * @param options            Animation options.
 * @param options.duration   Duration of the animation in milliseconds (default: 400).
 * @param options.display    Display property for the element (default: 'block').
 * @param options.minHeight  Minimum height of the element (default: 0).
 * @param options.transition CSS transition timing function (default: 'cubic-bezier(0.4, 0.0, 0.2, 1)').
 */
const slideDown = (
	element: HTMLElement,
	options: {
		duration?: number;
		display?: string;
		minHeight?: number;
		transition?: string;
	} = {}
): Promise<void> => {
	const {
		duration = 400,
		display = 'block',
		minHeight = 0,
		transition = 'cubic-bezier(0.4, 0.0, 0.2, 1)',
	} = options;

	return new Promise((resolve) => {
		const existing = animatingElements.get(element);
		if (existing) {
			existing.cancel();
		}

		element.hidden = false;
		element.style.display = display;

		let startHeight = element.getBoundingClientRect().height;

		element.style.height = 'auto';
		const endHeight = element.getBoundingClientRect().height;

		if (startHeight === endHeight) {
			startHeight = minHeight;
		}

		// Already expanded
		if (startHeight >= endHeight) {
			element.style.removeProperty('overflow');
			resolve();
			return;
		}

		let finished = false;

		const cleanup = () => {
			element.style.removeProperty('transition-property');
			element.style.removeProperty('transition-duration');
			element.style.removeProperty('transition-timing-function');
			animatingElements.delete(element);
		};

		const finish = () => {
			if (finished) return;
			finished = true;

			if (Math.abs(element.getBoundingClientRect().height - endHeight) < 1) {
				element.style.removeProperty('overflow');
			}

			element.style.height = 'auto';
			cleanup();
			resolve();
		};

		const cancel = () => {
			if (finished) return;
			finished = true;
			cleanup();
			resolve();
		};

		animatingElements.set(element, { cancel });

		element.style.height = startHeight + 'px';
		element.style.overflow = 'hidden';

		void element.offsetHeight;

		element.style.transitionProperty = 'height';
		element.style.transitionDuration = `${duration}ms`;
		element.style.transitionTimingFunction = transition;

		// double nest to ensure the browser has fully painted before applying end styles.
		requestAnimationFrame(() => {
			if (finished) return;

			requestAnimationFrame(() => {
				if (finished) return;

				element.style.height = endHeight + 'px';

				const totalDuration = parseComputedDuration(element);
				setTimeout(finish, totalDuration);
			});
		});
	});
};

const getOffsetTop = (element: HTMLElement | null) => {
	let offsetTop = 0;
	while (element) {
		offsetTop += element.offsetTop;
		element = element.offsetParent as HTMLElement;
	}
	return offsetTop;
};

// Touch devices with cursor support (e.g. Surface Pro) are not classified as touch devices for our purposes.
const isTouchDevice = () => {
	return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
};

const isMobileViewport = (breakpoints: IBreakpoints) => {
	const mediaQuery = breakpoints
		? `(max-width: ${parseInt(breakpoints.xl) - 1}px)`
		: '(max-width: 1199px)';
	return window.matchMedia(mediaQuery).matches;
};

const isElementVisible = (element: HTMLElement): boolean => {
	if (!(element instanceof Element)) return false;

	// Direct hidden cases
	if (element.matches('input[type="hidden"]')) return false;
	if (element.hasAttribute('hidden')) return false;

	// Computed style on self
	const style = getComputedStyle(element);
	if (style.display === 'none' || style.visibility === 'hidden') return false;

	// No box / zero rects (covers width/height 0, collapsed content, closed <details>, etc.)
	if (element.getClientRects().length === 0) return false;

	// Ancestor checks
	let parent = element.parentElement;
	while (parent) {
		const parentStyle = getComputedStyle(parent);
		if (
			parentStyle.display === 'none' ||
			parentStyle.visibility === 'hidden' ||
			parent.hasAttribute('hidden')
		) {
			return false;
		}
		parent = parent.parentElement;
	}

	return true;
};

/**
 * Wrapper for querySelectorAll that runs a callback on page load AND whenever
 * a Convert experience is activated via the _conv_q listener.
 *
 * @param target   The CSS selector to query.
 * @param callback The callback to run on freshly matched elements.
 * @param event
 */
const onConvertChanged = (
	target: string,
	callback: (elements: HTMLElement[]) => void,
	event = 'experience.activated'
): void => {
	const seen = new Set<HTMLElement>();

	const run = () => {
		const matches = Array.from(document.querySelectorAll<HTMLElement>(target));
		const fresh = matches.filter((el) => !seen.has(el));
		if (fresh.length > 0) {
			fresh.forEach((el) => seen.add(el));
			callback(fresh);
		}
	};

	// Initial pass
	run();

	// Re-run when Convert event runs
	// eslint-disable-next-line camelcase
	window._conv_q = window._conv_q || [];
	window._conv_q.push({
		what: 'addListener',
		params: {
			event,
			handler: () => run(),
		},
	});
};

export {
	slideUp,
	slideDown,
	getOffsetTop,
	isTouchDevice,
	isMobileViewport,
	isElementVisible,
	onConvertChanged,
};
